// src/App.jsx
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import Navbar from "./components/Navbar";
import Landing from "./pages/Landing";
import { SignupPage, LoginPage } from "./pages/Auth";
import LinkedInCallback from "./pages/LinkedInCallback";
import CompleteProfile from "./pages/CompleteProfile";
import Feed from "./pages/Feed";
import NeedDetail from "./pages/NeedDetail";
import PostNeed from "./pages/PostNeed";
import { MyNeeds, PitchesReceived } from "./pages/CorpPages";
import MyPitches from "./pages/MyPitches";
import ProblemFramer from "./pages/ProblemFramer";
import AdminDashboard from "./pages/AdminDashboard";
import "./index.css";

function homePath(effectiveRole) {
  if (effectiveRole === "admin") return "/admin";
  if (effectiveRole === "corporation") return "/my-needs";
  return "/feed";
}

function PrivateRoute({ children, role, adminOnly }) {
  const { currentUser, userProfile, effectiveRole, isAdmin, viewMode, needsOnboarding } = useAuth();
  if (!currentUser) return <Navigate to="/login" replace />;
  if (needsOnboarding || !userProfile) return <Navigate to="/complete-profile" replace />;
  if (adminOnly) {
    if (!isAdmin || viewMode !== "admin") return <Navigate to={homePath(effectiveRole)} replace />;
    return children;
  }
  if (role && effectiveRole !== role) return <Navigate to={homePath(effectiveRole)} replace />;
  return children;
}

function AppRoutes() {
  const { currentUser, userProfile, effectiveRole, needsOnboarding } = useAuth();

  return (
    <>
      <Navbar />
      <Routes>
        <Route
          path="/"
          element={
            currentUser
              ? (needsOnboarding || !userProfile
                ? <Navigate to="/complete-profile" replace />
                : <Navigate to={homePath(effectiveRole)} replace />)
              : <Landing />
          }
        />
        <Route
          path="/signup"
          element={
            currentUser
              ? (needsOnboarding || !userProfile
                ? <Navigate to="/complete-profile" replace />
                : <Navigate to={homePath(effectiveRole)} replace />)
              : <SignupPage />
          }
        />
        <Route
          path="/login"
          element={
            currentUser
              ? (needsOnboarding || !userProfile
                ? <Navigate to="/complete-profile" replace />
                : <Navigate to={homePath(effectiveRole)} replace />)
              : <LoginPage />
          }
        />
        <Route
          path="/complete-profile"
          element={
            !currentUser
              ? <Navigate to="/login" replace />
              : userProfile
                ? <Navigate to={homePath(effectiveRole)} replace />
                : <CompleteProfile />
          }
        />
        <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />

        <Route path="/feed" element={<PrivateRoute role="startup"><Feed /></PrivateRoute>} />
        <Route path="/my-pitches" element={<PrivateRoute role="startup"><MyPitches /></PrivateRoute>} />

        <Route path="/need/:id" element={<PrivateRoute><NeedDetail /></PrivateRoute>} />

        <Route path="/my-needs" element={<PrivateRoute role="corporation"><MyNeeds /></PrivateRoute>} />
        <Route path="/frame-problem" element={<PrivateRoute role="corporation"><ProblemFramer /></PrivateRoute>} />
        <Route path="/post-need" element={<PrivateRoute role="corporation"><PostNeed /></PrivateRoute>} />
        <Route path="/pitches-received" element={<PrivateRoute role="corporation"><PitchesReceived /></PrivateRoute>} />

        <Route path="/admin" element={<PrivateRoute adminOnly><AdminDashboard /></PrivateRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
